// en/views/channelOta.js
export const channelOta = {
  common: {
    listing: "Listing",
    back: "Back",
    close: "Close",
    cancel: "Cancel",
    flexible: "Flexible",
    noResults: "No matches",
    deleteConn: "Delete connection",
  },
  head: {
    manage: "Manage {channel}",
    airbnbConfig: "Airbnb settings",
    channelOptions: "Channel options",
  },
  picker: {
    placeholder: "Search or pick a listing…",
  },
  loading: {
    status: "Loading channel status…",
    listings: "Loading your Airbnb listings…",
    settings: "Loading listing settings…",
  },
  accounts: {
    title: "Which of your {channel} accounts?",
    lead: "You already have one {channel} account connected. You can add this listing to it, or connect a different account. | You already have {n} {channel} accounts connected. Choose which one this listing belongs to, or connect another.",
    account: "Account {n}",
    empty: "No listings yet",
    andMore: "{list} and {rest} more",
    newTitle: "Connect another {channel} account",
    newDesc: "For listings that live in a different account",
  },
  confirm: {
    title: "Delete {channel} connection",
    desc: "The channel will be removed from the channel manager and from your active connections.",
    coversPre: "This connection covers ",
    coversCount: "{count} listing | {count} listings",
    coversPost: " and will be disconnected from all of them: {list}",
    purgePre: "Delete imported reservations too? Choose which rooms ",
    purgeNote: "(they can be recovered)",
    selectAll: "All",
    unselectAll: "Clear all",
    delete: "Delete",
    detachOnly: "Disconnect this listing only",
    deleteAll: "Delete and disconnect {count} listing | Delete and disconnect all {count} listings",
    deleteWithResv: "Delete (+{count} reservation) | Delete (+{count} reservations)",
  },
  gone: {
    title: "This connection no longer exists on the channel",
    desc: "The channel was deleted in the channel manager. Delete this connection to reconnect it from “Connect now”.",
  },
  menu: {
    channelOptDesc: "Turn on or pause, edit the mapping, sync, pull reservations or delete the connection.",
    airbnbCfgDesc: "Check-in/out times, notice, prices and cleaning for each listing.",
  },
  channel: {
    statusLabel: "Channel status",
    active: "Active",
    inactive: "Inactive",
    deactivate: "Deactivate channel",
    activate: "Activate channel",
    pending: "Under review",
    hintPending: "The channel is connected and already has your data. It still has to review and accept your property; once it does, it activates on its own. It usually takes a few days. If it takes longer, write to us and we will look into it.",
    hintActive: "The channel is active: we send prices and availability. Deactivate it to pause sending without losing the mapping.",
    blockersTitle: "Something is missing before the channel can go live",
    blockersHint: "Press “Connect” again in the wizard and we will fix it and resubmit.",
    hintInactive: "The channel is connected but paused. Activate it to start sending prices and availability.",
    editTitle: "Edit channel",
    editDesc: "Change or add rooms and rate plans mapped to this channel.",
    editBtn: "Edit mapping",
    fullSyncTitle: "Full sync",
    fullSyncDesc: "Resends all prices, availability and restrictions to the channel.",
    synced: "Synced",
    sync: "Sync",
    extractTitle: "Pull future reservations",
    extractDesc: "Import the reservations you already have on the channel into WACheckin.",
    imported: "Imported",
    extract: "Pull",
    resyncTitle: "Re-import reservations",
    resyncDesc: "Fetch all the channel's reservations again and re-assign them with the current mapping. Use it if you picked the wrong listing or reservations are missing.",
    resync: "Re-import",
  },
  cfg: {
    emptyNoListings: "You haven't matched any listing yet. Go to <b>Channel options → Edit mapping</b> to connect your Airbnb listings with your units.",
    emptyNoMapping: "This property doesn't have any Airbnb listing matched yet. Go to <b>Channel options → Edit mapping</b> and match its listing to set its times and prices here.",
  },
  arrival: {
    section: "Arrival and departure",
    lead: "What times and days your guests can check in and out.",
    checkInFrom: "Check-in from",
    checkInFromTip: "Earliest time a guest can check in.",
    checkInTo: "Check-in until",
    checkInToTip: "Latest time a guest can arrive.",
    checkOut: "Check-out",
    checkOutTip: "Latest time the guest must leave the property.",
    unset: "Not set",
    daysIn: "Days check-in is allowed",
    daysInTip: "Days of the week when a guest can check in.",
    daysOut: "Days check-out is allowed",
    daysOutTip: "Days of the week when a guest can check out.",
  },
  booking: {
    section: "Reservations",
    window: "Availability window",
    windowTip: "How far in advance your calendar opens for sale. It's what Airbnb calls the 'availability window' (12 months = 365 days). It is NOT the maximum nights per stay.",
    minLead: "Minimum notice",
    minLeadTip: "How long before arrival a booking can be made. Prevents last-minute bookings.",
    maxStay: "Maximum stay",
    maxStayTip: "Maximum number of nights per reservation. It's Airbnb's 'maximum nights', different from the availability window.",
    turnover: "Days between reservations",
    turnoverTip: "Days blocked between a checkout and the next check-in, to clean and prepare.",
  },
  rules: {
    section: "Booking rules",
    cancelPolicy: "Cancellation policy",
    cancelPolicyTip: "How much the guest can recover if they cancel. Flexible = more lenient; Strict = less refund.",
    whoCanBook: "Who can book",
    whoCanBookTip: "Who can book instantly without your prior approval.",
  },
  prices: {
    section: "Prices",
    perNight: "Price per night",
    weekend: "Weekend price",
    weekendTip: "Price for Friday and Saturday. Leave it the same as the nightly price if you don't want a difference.",
    guestsIncluded: "Guests included",
    guestsIncludedTip: "Number of guests at no extra cost.",
    extraGuest: "Extra guest price",
    extraGuestTip: "Cost per additional guest, per night, beyond those included.",
  },
  cleaning: {
    section: "Cleaning",
    notePre: "Fill in both so every reservation pays for cleaning. The long-stay fee must be ",
    noteStrong: "equal to or higher",
    notePost: " than the short-stay one. If you leave one at 0, that stay type won't pay for cleaning.",
    long: "Cleaning · long stay",
    longTip: "Charged on long stays. Must be equal to or higher than the short-stay fee.",
    short: "Cleaning · short stay",
    shortTip: "Charged on short stays (a few nights).",
    error: "The long-stay cleaning fee ({sym}{long}) must be equal to or higher than the short-stay one ({sym}{short}).",
  },
  tax: {
    section: "Tourist tax",
    lead: "Local tax you charge the guest and report yourself (not Airbnb). Leave it at 0 if it doesn't apply.",
    amount: "Amount",
    amountTip: "Tax amount. Set 0 to not apply it.",
    charged: "Charged",
    chargedTip: "How the tax is calculated: per guest per night, per night, or per guest.",
    perGuestNight: "Per guest per night",
    perNight: "Per night",
    perGuest: "Per guest",
    attestation: "I confirm this information is correct and that I take responsibility for charging and reporting this tax.",
  },
  footer: {
    save: "Save settings",
    saving: "Saving…",
  },
  opts: {
    noticeNoLimit: "No limit (all dates)",
    months: "{count} month | {count} months",
    maxNoLimit: "No limit",
    nights: "{count} night | {count} nights",
    maxNights: "Maximum ({count})",
    cancelModerate: "Moderate",
    cancelFirm: "Firm",
    cancelStrict: "Strict",
    instantEveryone: "Any guest",
    instantWellReviewed: "Well-reviewed guests",
    instantVerified: "Verified identity",
    instantReviewedVerified: "Reviews + identity",
    leadNone: "No notice",
    hours: "{count} hour | {count} hours",
    days: "{count} day | {count} days",
  },
  msg: {
    loadSettingsError: "Couldn't load the listing settings.",
    taxAttestationWarn: "Tick the attestation box to apply the tourist tax.",
    saveOk: "Airbnb settings saved and sent to the channel.",
    saveError: "Couldn't save the settings.",
    statusError: "Couldn't load the channel status.",
    activated: "Channel activated.",
    deactivated: "Channel deactivated.",
    toggleError: "Couldn't change the channel status.",
    resyncOk: "Reservations re-imported: {imported}.",
    resyncOkWarn: "Reservations re-imported: {imported} ({errors} with a warning).",
    resyncError: "Couldn't re-import the reservations.",
    disconnectError: "Couldn't delete the connection.",
    fullSyncOk: "Full sync sent.",
    extractOk: "Future reservations imported.",
    actionError: "Couldn't run the action.",
  },
  rate: {
    apply: "Apply",
    or: "or",
    usePlan: "use one of your rates…",
  },
};
